'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { ApplicationServiceContainer } from '../../infrastructure/container/DIContainer';
import { OrderService } from '../../infrastructure/services/OrderService';
import { ProductService } from '../../infrastructure/services/ProductService';
import { CartService } from '../services/CartService';
import { OrderRepository } from '../../domain/repositories/OrderRepository';
import { ProductRepository } from '../../domain/repositories/ProductRepository';
import type { ICartRepository } from '../../domain/repositories/ICartRepository';

/**
 * Service context interface
 * Provides access to all application services through dependency injection
 */
interface ServiceContextType {
  orderService: OrderService;
  productService: ProductService;
  cartService: CartService;
  orderRepository: OrderRepository;
  productRepository: ProductRepository;
  cartRepository: ICartRepository;
}

const ServiceContext = createContext<ServiceContextType | null>(null);

/**
 * React Provider for dependency injection
 * Wraps the DIContainer in a React context for easy access throughout the app
 */
interface ServiceProviderProps {
  children: ReactNode;
  container?: ApplicationServiceContainer; // Allow injection for testing
}

export function ServiceProvider({ children, container }: ServiceProviderProps) {
  const serviceContainer = container || ApplicationServiceContainer.getSharedInstance();

  const services: ServiceContextType = {
    orderService: serviceContainer.resolveService<OrderService>('OrderService'),
    productService: serviceContainer.resolveService<ProductService>('ProductService'),
    cartService: serviceContainer.resolveService<CartService>('CartService'),
    orderRepository: serviceContainer.resolveOrderRepository(),
    productRepository: serviceContainer.resolveProductRepository(),
    cartRepository: serviceContainer.resolveCartRepository(),
  };

  return <ServiceContext.Provider value={services}>{children}</ServiceContext.Provider>;
}

/**
 * Hook to access all services
 */
export function useServices(): ServiceContextType {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useServices must be used within a ServiceProvider');
  }
  return context;
}

/**
 * Individual service hooks for specific use cases
 */
export function useOrderService(): OrderService {
  const { orderService } = useServices();
  return orderService;
}

export function useProductService(): ProductService {
  const { productService } = useServices();
  return productService;
}

export function useCartService(): CartService {
  const { cartService } = useServices();
  return cartService;
}

export function useOrderRepository(): OrderRepository {
  const { orderRepository } = useServices();
  return orderRepository;
}

export function useProductRepository(): ProductRepository {
  const { productRepository } = useServices();
  return productRepository;
}

/**
 * Higher-order component for injecting services as props
 * Useful for class components or components that need services as props
 */
interface WithServicesProps {
  services: ServiceContextType;
}

export function withServices<P extends object>(
  Component: React.ComponentType<P & WithServicesProps>,
): React.ComponentType<P> {
  return function WithServicesComponent(props: P) {
    const services = useServices();
    return <Component {...props} services={services} />;
  };
}

/**
 * Testing utilities
 */
export function createMockServiceProvider(mockServices: Partial<ServiceContextType>) {
  const defaultMocks: ServiceContextType = {
    orderService: {} as OrderService,
    productService: {} as ProductService,
    cartService: {} as CartService,
    orderRepository: {} as OrderRepository,
    productRepository: {} as ProductRepository,
    cartRepository: {} as ICartRepository,
    ...mockServices,
  };

  return function MockServiceProvider({ children }: { children: ReactNode }) {
    return <ServiceContext.Provider value={defaultMocks}>{children}</ServiceContext.Provider>;
  };
}
